import React from 'react';
import { ArrowLeft } from 'lucide-react';

/**
 * Shared page-layout kit — the single source of truth for the frame and header
 * chrome of every top-level page. Pages compose these instead of rolling their
 * own container widths and toolbars, so width, spacing, and header structure
 * stay consistent across the app.
 *
 * Width is intentionally fixed (one constrained max-width everywhere) so tabbing
 * between pages never shifts the content edges.
 */

/** The one content width used app-wide. Master–detail pages fill it too.
 *  Matches the App content cap (App.tsx) so every page lines up at one edge. */
export const PAGE_MAX_WIDTH = 'max-w-[1800px]';

interface PageContainerProps {
  children: React.ReactNode;
  className?: string;
  /**
   * `stack` (default): vertical rhythm via space-y-4 — for normal scrolling pages.
   * `fill`: no built-in spacing and full available height — for master–detail
   *   pages that manage their own internal grid/height.
   */
  variant?: 'stack' | 'fill';
}

export function PageContainer({ children, className = '', variant = 'stack' }: PageContainerProps) {
  const base = `mx-auto w-full ${PAGE_MAX_WIDTH}`;
  /**
   * ⚠️⚠️ **`space-y-4`, NOT `space-y-6` — AND THAT IS WHY THERE WAS A SECOND
   * CANVAS BUDGET** (owner, 2026-09-11).
   *
   * Every ActionShell page (Build, Quests, Abilities, Programs, Projects,
   * Tasks, Habits, Routines) renders its tab ribbon inside this container, and
   * Reflect renders an IDENTICAL 46px ribbon inside a plain `space-y-4` root.
   * The only difference between the two families was the 8px of gap under that
   * ribbon — and it is the entire reason `calc(100vh-11.5rem)` existed
   * alongside `calc(100vh-10.875rem)`. Measured: hub chrome 184px, Reflect
   * chrome 174px, both exactly matching their own constant.
   *
   * ⭐ So this is one number deleting a whole constant: eight pages get 8px of
   * height back and the budget vocabulary loses an entry. Every `11.5rem`
   * moved to `10.875rem` in the same commit — they are not independent.
   * ⚠️ The other caller of the `stack` variant is `RoutineExecution`, where the
   * gap sits under a `PageHeader` rather than a ribbon; 16px is the app's
   * standard panel gap there too (`gap-4` outnumbers `gap-6` 258 to 15).
   */
  const variantCls = variant === 'fill' ? 'h-full' : 'space-y-4';
  return <div className={`${base} ${variantCls} ${className}`}>{children}</div>;
}

interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Optional leading icon, rendered before the title. */
  icon?: React.ReactNode;
  /** Secondary controls (view/scope toggles, search) — shown before the actions. */
  filters?: React.ReactNode;
  /** Primary actions (e.g. a "New …" button) — trailing, right-aligned. */
  actions?: React.ReactNode;
  /**
   * Optional breadcrumb-style back link rendered above the title — the single
   * source of truth for "← Back to …" affordances so they look identical on
   * every page (Schedule → Calendar, Templates → Schedule, etc.).
   */
  backLink?: { label: string; onClick: () => void };
  className?: string;
  /** Tighten the title to text-xl (for denser pages). */
  compact?: boolean;
}

/**
 * Standard page header: title (+ optional icon/description) on the left, optional
 * filters and primary actions on the right. Stacks vertically on small screens.
 */
export function PageHeader({
  title,
  description,
  icon,
  filters,
  actions,
  backLink,
  className = '',
  compact = false,
}: PageHeaderProps) {
  return (
    <header className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${className}`}>
      <div className="flex min-w-0 flex-col gap-1">
        {backLink && (
          <button
            type="button"
            onClick={backLink.onClick}
            className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {backLink.label}
          </button>
        )}
        <div className="flex min-w-0 items-center gap-2.5">
          {icon && <span className="shrink-0 text-primary">{icon}</span>}
          <div className="min-w-0">
            <h1 className={`font-bold text-foreground ${compact ? 'text-xl' : 'text-2xl'}`}>{title}</h1>
            {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
          </div>
        </div>
      </div>

      {(filters || actions) && (
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          {filters}
          {actions}
        </div>
      )}
    </header>
  );
}

interface SectionHeaderProps {
  label: React.ReactNode;
  /** Category/accent colour dot before the label. */
  color?: string;
  count?: number;
  /** Trailing content (e.g. a small action or toggle). */
  right?: React.ReactNode;
  className?: string;
}

/** Consistent group/category header (the coloured-dot + label rows in lists). */
export function SectionHeader({ label, color, count, right, className = '' }: SectionHeaderProps) {
  return (
    <div className={`flex items-center justify-between ${className}`}>
      <div className="flex min-w-0 items-center gap-2">
        {color && <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />}
        <h2 className="truncate text-sm font-semibold text-foreground">{label}</h2>
        {count != null && <span className="shrink-0 text-xs text-muted-foreground">({count})</span>}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  /**
   * Vertically center within a tall canvas (≈60vh) instead of just padding —
   * use when the empty state IS the page/section body, so it doesn't float at
   * the top. Omit for empty states nested in a small card.
   */
  fill?: boolean;
}

/** Consistent centered empty / "select a …" state. */
export function EmptyState({ icon, title, description, action, className = '', fill = false }: EmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center px-6 text-center ${
        fill ? 'min-h-[60vh] py-12' : 'py-16'
      } ${className}`}
    >
      {icon && <div className="mb-4 text-muted-foreground/70">{icon}</div>}
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      {description && <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
